/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

// INTERVIEW ESCALATIONS AS RISK FACTORS (v1_851). An interview escalation that
// fired sets the tier (sharedContactChannel lifts a case to High), so it must
// be on the officer's Risk Factors, marked Raised in interview, with what it
// fired on and any explanation the client gave. Until v1_851 it reached the
// officer only as "escalation override" on the tier panel, with nothing naming
// it (production 28/09/26). Pure.

// The escalations the interview's own checks raise (the portal's live check
// each turn and the retier on every run). Registry and screening escalations
// have their own panels.
export const INTERVIEW_ESCALATIONS = Object.freeze(['sharedContactChannel', 'applicantEntityMismatch']);

// The explanation the interview recorded for this escalation, if any: an
// inconsistency Alex raised and the client explained that names the same
// address, or the same company names.
function explanationFor(esc, inconsistencyFlags) {
  const ev = String(esc.evidence || '');
  const emails = ev.match(/[^\s(),]+@[^\s(),]+/g) || [];
  const quoted = (ev.match(/"([^"]+)"/g) || []).map(q => q.slice(1, -1).toLowerCase());
  for (const f of (Array.isArray(inconsistencyFlags) ? inconsistencyFlags : [])) {
    if (!f || !f.explanation) continue;
    const t = String(f.text || '').toLowerCase();
    if (emails.some(e => t.includes(e.toLowerCase())) || (quoted.length && quoted.every(q => t.includes(q)))) return f.explanation;
    if (esc.trigger === 'sharedContactChannel' && /(share|same|one)\s+(e-?mail|inbox|address)/.test(t)) return f.explanation;
  }
  return null;
}

// The Risk Factors panel's flag model ({text, detail, source}) for each fired
// interview escalation. The text is stable (label and evidence) so an officer's
// note lines up on it across re-tiers.
export function escalationFlagModels(assessment, inconsistencyFlags) {
  const escs = ((assessment && assessment.escalations) || []).filter(e => e && INTERVIEW_ESCALATIONS.includes(e.trigger));
  return escs.map(e => {
    const expl = explanationFor(e, inconsistencyFlags);
    const detail = (expl ? `Client's explanation: ${expl}\n\n` : 'No explanation was recorded in the interview.\n\n') +
      `This moved the case to ${e.tier} risk${e.citation ? ' (' + e.citation + ')' : ''}. It is re checked on the file every time the case is re tiered.`;
    return { text: e.label + (e.evidence ? ': ' + e.evidence : ''), detail, source: 'interview', escalation: e.trigger };
  });
}
